import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { Checkbox } from '@lmc/ui-kit/src/dataDisplay/Checkbox'
import { Text } from '@lmc/ui-kit/src/general'
import type { BodyMapPickerEntry } from '@lmc/validations'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import BodyMapItem, { DateLabel } from './BodyMapItem'

const DATE_FORMAT = 'DD-MM-YY, HH:mm'

export type BodyMapGroupProps = {
  entry: BodyMapPickerEntry
  expandedReportIds: string[]
  onToggleGroup: (reportIds: string[], shouldSelect: boolean) => void
  onToggleReportExpanded: (reportId: string) => void
  onToggleReportSelected: (reportId: string) => void
  selectedReportIds: string[]
  testId?: string
}

const Wrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: ${({ theme }) => theme.properties.borderRadius.small};
  overflow: hidden;
`

const Header = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.color.neutral.color10};
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
  display: flex;
  gap: 16px;
  padding: 8px 12px;
`

// Colour set on the wrapper rather than per line: Text applies `all: unset`,
// so it cascades in. A label tied to the checkbox, so the title and date are
// part of the select-all hit area.
const HeaderCopy = styled.label`
  color: ${({ theme }) => theme.color.neutral.color4};
  cursor: pointer;
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  min-width: 0;
`

const CollapseToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 16px;
  line-height: 0;
  padding: 0;
`

const Members = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 12px;
`

/**
 * A set of body maps recorded together in one questionnaire submission, so the
 * header checkbox selects the group as a unit while members stay individually
 * selectable.
 */
const BodyMapGroup = ({
  entry,
  expandedReportIds,
  onToggleGroup,
  onToggleReportExpanded,
  onToggleReportSelected,
  selectedReportIds,
  testId
}: BodyMapGroupProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  // Groups start open; collapsing is the affordance, not the default.
  const [isOpen, setIsOpen] = useState(true)

  const memberIds = entry.reports.map((report) => report.id)
  const checkboxId = `body-map-group-select-${entry.groupId}`
  const selectedCount = memberIds.filter((id) =>
    selectedReportIds.includes(id)
  ).length
  const allSelected = selectedCount === memberIds.length && memberIds.length > 0

  return (
    <Wrapper data-testid={testId}>
      <Header>
        <Checkbox
          checked={allSelected}
          data-testid={testId && `${testId}-select-all`}
          id={checkboxId}
          indeterminate={selectedCount > 0 && !allSelected}
          onChange={() => onToggleGroup(memberIds, !allSelected)}
        />
        <HeaderCopy htmlFor={checkboxId}>
          <Text level={1} weight="medium">
            {t(incidentReportsConstants.MULTIPLE_MARKS_AND_INJURIES, {
              count: entry.reports.length
            })}
          </Text>
          <DateLabel>
            {`${t(incidentReportsConstants.UPDATED_ON)} ${dayjs(entry.updatedOn).format(DATE_FORMAT)}`}
          </DateLabel>
        </HeaderCopy>
        <CollapseToggle
          aria-label={t(
            isOpen
              ? incidentReportsConstants.COLLAPSE_BODY_MAP_GROUP
              : incidentReportsConstants.EXPAND_BODY_MAP_GROUP
          )}
          data-testid={testId && `${testId}-collapse-toggle`}
          onClick={() => setIsOpen((open) => !open)}
          type="button"
        >
          {isOpen ? <UpOutlined /> : <DownOutlined />}
        </CollapseToggle>
      </Header>
      {isOpen && (
        <Members>
          {entry.reports.map((report) => (
            <BodyMapItem
              expanded={expandedReportIds.includes(report.id)}
              key={report.id}
              nested
              onToggleExpanded={onToggleReportExpanded}
              onToggleSelected={onToggleReportSelected}
              report={report}
              selected={selectedReportIds.includes(report.id)}
              testId={testId && `${testId}-item-${report.id}`}
            />
          ))}
        </Members>
      )}
    </Wrapper>
  )
}

export default BodyMapGroup
